import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import WebGLVectorLayer from 'ol/layer/WebGLVector.js';
import VectorSource from 'ol/source/Vector.js';
import { unByKey } from 'ol/Observable';
import { LuFileDown } from 'react-icons/lu';
//import { LuLayoutGrid } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { getRotatedGrid, getDSP } from '@utils/api';
import { deleteLayerByCustomId, getLayerByCustomId } from '@utils/map/helpers';
import { createTooltip } from '@utils/map/tooltip.overlay';

import LayerSettings from '../../sidebars/sidebarElements/LayerSettings';
import { FootprintTooltip } from './OverlayTooltips';
//import DspColorFilter from '../../modifyLayers/dspColorFilter/DspColorFilter';
import EstimateWarning from './EstimateWarning';
import { toObjectUrl, downloadFile } from '@utils/files';

import { parseFootprintData, parseDspData, handleFootprintFeatureHover } from './helpers';

const defaultVisible = false;
export const defaultStyle = {
	'fill-color': [
		'case',
		['>', ['get', 'hovering'], 0], // Changed from == true to > 0
		'rgba(0, 0, 0, 0.5)',
		'rgba(0, 0, 0, 0.3)',
	],
	'stroke-color': [
		'case',
		['>', ['get', 'hovering'], 0], // Changed from == true to > 0
		'rgba(255, 255, 0, 0.8)',
		'rgba(0, 0, 0, 0.8)',
	],
	'stroke-width': 2,
};

//const isDevMode = process.env.NODE_ENV === 'development';

const SingleImageFootprinsLayer = ({
	layerId = 'singleImageFootprintsLayer',
	layerName = 'Footprints',
	showDsp = false,
	setDspColorFilterActive,
	dspColorFilterSidebarId,
}) => {
	const featureType = showDsp ? 'digitalSamplePlot' : 'footprint';

	const {
		mapObject,
		project,
		pickedTask,
		colorScheme,
		toolBarVisible,
		pickedModelClasses,
		dspFeaturesInfo,
		dispatch,
	} = useProject();
	const { uuid: projectUuid } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const adding = useRef(false);

	const hoveredFeature = useRef(null);
	const tooltipContainerRef = useRef(null);
	const [tooltipContent, setTooltipContent] = useState(null);

	const [singleImageFootprintsLayer, setSingleImageFootprintsLayer] = useState(null);
	const [features, setFeatures] = useState([]);

	const { data } = useQuery({
		queryKey: ['fetchRotatedGrid', projectUuid, modelUuid],
		queryFn: () => getRotatedGrid(projectUuid),
		enabled: !!project?.uuid && !!mapObject && !showDsp,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const { data: dspData } = useQuery({
		queryKey: ['fetchDsp', projectUuid, modelUuid],
		queryFn: () => getDSP(projectUuid, modelUuid),
		enabled:
			!!project?.uuid && !!mapObject && showDsp && modelUuid && !!pickedModelClasses?.length,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const geojsonUrl = useMemo(() => toObjectUrl(dspData), [dspData]);
	const downloadGeojson = () =>
		downloadFile({
			url: geojsonUrl,
			title: `${project?.title}_DSP`,
			fileExtension: 'geojson',
		});

	useEffect(() => {
		if (showDsp || !data) return;

		const parsedFeatures = parseFootprintData({ data, featureType });
		if (parsedFeatures) {
			setFeatures(parsedFeatures);
		}

		return () => {
			setFeatures([]);
		};
	}, [data, showDsp]);

	useEffect(() => {
		if (!showDsp || !dspData || !dspFeaturesInfo) return;

		const parsedFeatures = parseDspData({
			data: dspData,
			featureType,
			dspFeaturesInfo,
			pickedModelClasses,
		});
		if (parsedFeatures) {
			setFeatures(parsedFeatures);
		}

		return () => {
			setFeatures([]);
		};
	}, [showDsp, dspData, pickedModelClasses, dspFeaturesInfo]);

	const collectDspFeaturesInfo = useCallback(() => {
		if (!data) return;

		// The endpoint for getting dsp data does not have alle the info we need
		// so we need to get the features from the rotated grid endpoint

		const featuresData = data?.features || [];

		const items = featuresData.map(feature => {
			const coordinates = feature.geometry?.coordinates;
			if (!coordinates) {
				return null;
			}

			const { properties } = feature || {};

			const _lrfActive = properties.image_location?.lrf_active;

			return {
				tile_name: properties.tile_name,
				lrfActive: _lrfActive,
			};
		});

		const filteredItems = items.filter(item => item);
		dispatch({
			type: 'setDspFeaturesInfo',
			payload: filteredItems,
		});
	}, [data]);

	useEffect(() => {
		collectDspFeaturesInfo();
	}, [collectDspFeaturesInfo]);

	const hasRoughEstimate = useMemo(() => {
		if (!dspFeaturesInfo?.length) return false;
		return dspFeaturesInfo.some(item => item.lrfActive === false);
	}, [dspFeaturesInfo]);

	const handleFeatureHoverCallback = useCallback(
		(feature, tooltip) => {
			handleFootprintFeatureHover({
				feature,
				tooltip,
				mapObject,
				toolBarVisible,
				featureType,
				hoveredFeature,
				setTooltipContent,
			});
		},
		[mapObject, toolBarVisible, featureType, dspData, data]
	);

	const updateFootprintLayer = useCallback(() => {
		if (mapObject && !adding.current) {
			adding.current = true;

			const existingLayer = getLayerByCustomId(mapObject, layerId);

			if (!features?.length) {
				adding.current = false;

				if (existingLayer) {
					deleteLayerByCustomId(mapObject, layerId);
				}

				return;
			}

			const footprintVectorSource = new VectorSource({
				features: features,
			});

			let currentLayer = null;

			if (existingLayer) {
				currentLayer = existingLayer;
				currentLayer.setSource(footprintVectorSource);
			} else {
				currentLayer = new WebGLVectorLayer({
					source: footprintVectorSource,
					zIndex: 11,
					name: layerName,
					properties: {
						customLayerId: layerId,
					},
					style: defaultStyle,
					visible: defaultVisible,
				});

				mapObject.addLayer(currentLayer);
				console.log(`Added ${layerId} layer`);
			}

			setSingleImageFootprintsLayer(currentLayer);
			adding.current = false;
		}
	}, [features]);

	useEffect(() => {
		updateFootprintLayer();
	}, [updateFootprintLayer]);

	useMapTooltip({
		mapObject,
		tooltipContainerRef,
		layerId,
		onFeatureHover: handleFeatureHoverCallback,
	});

	if (!singleImageFootprintsLayer) return null;

	return (
		<li>
			<div className="d-flex flex-row gap-2 align-items-center">
				<LayerSettings
					id={layerId}
					layer={singleImageFootprintsLayer}
					label={singleImageFootprintsLayer.get('name')}
					defaultState={singleImageFootprintsLayer?.getVisible()}
					onCheck={() => {
						singleImageFootprintsLayer?.setVisible(true);
					}}
					onUncheck={() => {
						singleImageFootprintsLayer?.setVisible(false);
					}}
					disabled={!singleImageFootprintsLayer}
					layerFunctions={[
						{
							title: 'Export GeoJSON',
							icon: <LuFileDown />,
							onClick: downloadGeojson,
						},
					]}
					// layerFunctions={
					// 	showDsp && isDevMode
					// 		? [
					// 				{
					// 					title: 'Color Filter',
					// 					icon: <LuLayoutGrid />,
					// 					onClick: setDspColorFilterActive,
					// 				},
					// 			]
					// 		: []
					// }
					layerExplanations={hasRoughEstimate ? [<EstimateWarning />] : []}
				/>
			</div>

			<div ref={tooltipContainerRef} className="ol-tooltip">
				<FootprintTooltip {...tooltipContent} colorScheme={colorScheme} />
			</div>

			{/* {showDsp && isDevMode && (
				<DspColorFilter
					sidebarId={dspColorFilterSidebarId}
					dspData={dspData}
					dspFootprintLayer={singleImageFootprintsLayer}
				/>
			)} */}
		</li>
	);
};
export default SingleImageFootprinsLayer;

const useMapTooltip = ({ mapObject, tooltipContainerRef, layerId, onFeatureHover }) => {
	const tooltipRef = useRef(null);
	const pointermoveKeyRef = useRef(null);

	useEffect(() => {
		// Clean up previous listener if it exists
		if (pointermoveKeyRef.current) {
			unByKey(pointermoveKeyRef.current);
			pointermoveKeyRef.current = null;
		}

		// Return early if required dependencies aren't ready
		if (!mapObject || !tooltipContainerRef.current) return;

		// Initialize tooltip if not already created
		if (!tooltipRef.current) {
			tooltipRef.current = createTooltip({
				mapRef: mapObject,
				tooltipRef: tooltipContainerRef.current,
				id: `image-footprint-tooltip-${layerId}`,
			});
		}

		// Set up pointer move handler
		const handlePointerMove = event => {
			if (!mapObject || !tooltipRef.current) return;

			const feature = mapObject.forEachFeatureAtPixel(event.pixel, feature => feature);

			onFeatureHover(feature, tooltipRef.current);
		};

		// Attach event listener
		pointermoveKeyRef.current = mapObject.on('pointermove', handlePointerMove);

		return () => {
			if (pointermoveKeyRef.current) {
				unByKey(pointermoveKeyRef.current);
				pointermoveKeyRef.current = null;
			}
		};
	}, [mapObject, tooltipContainerRef.current, layerId, onFeatureHover]); // Added .current to tooltipContainerRef

	return tooltipRef;
};
